import { Box, Flex, Heading, HStack, Link, LinkProps, Text, TextProps } from '@chakra-ui/react';
import { GuideProductsSection, GuideProductsData } from './Product';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown } from '@fortawesome/pro-regular-svg-icons/faArrowDown';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { MutableRefObject, useRef } from 'react';
import { _js } from '@ifixit/localize';

export interface ComponentData {
   productData: GuideProductsData;
   introData: GuideIntroData;
}
interface GuideIntroData {
   introHtml: string;
}

export function GuideProducts(props: ComponentData) {
   const hasIntro = Boolean(props.introData.introHtml);

   const introRef = useRef<HTMLDivElement>(undefined);

   if (!hasIntro) {
      return (
         <Flex flexDir={{ base: 'column-reverse', sm: 'row' }}>
            {/* @ts-expect-error TS(2322) FIXME: Type 'MutableRefObject<HTMLDivElement | undefined>... Remove this comment to see the full error message */}
            <GuideItems hasIntro={hasIntro} {...props.productData} introRef={introRef}></GuideItems>
            <Flex flexDir={{ base: 'column-reverse', sm: 'column' }} flex={1}></Flex>
         </Flex>
      );
   }

   return (
      <Flex flexDir={{ base: 'column' }}>
         <Flex flexDir={{ base: 'column-reverse', sm: 'row' }}>
            {/* @ts-expect-error TS(2322) FIXME: Type 'MutableRefObject<HTMLDivElement | undefined>... Remove this comment to see the full error message */}
            <GuideIntro {...props.introData} introRef={introRef}></GuideIntro>
            {/* @ts-expect-error TS(2322) FIXME: Type 'MutableRefObject<HTMLDivElement | undefined>... Remove this comment to see the full error message */}
            <GuideItems hasIntro={hasIntro} {...props.productData} introRef={introRef}></GuideItems>
         </Flex>
      </Flex>
   );
}

function GuideIntro({
   introHtml,
   introRef,
}: GuideIntroData & { introRef: MutableRefObject<HTMLDivElement> }) {
   if (!introHtml) {
      return null;
   }

   return (
      <Flex
         flexDir={{ base: 'column-reverse', sm: 'column' }}
         flex={1}
         alignSelf="flex-start"
         position="sticky"
         top={0}
      >
         <Flex
            flexDir="column"
            margin={0}
            paddingY={4}
            className="introduction-container prose-wiki"
         >
            <HStack
               spacing={{ base: 0, sm: 3 }}
               marginBottom={{ base: '12px', sm: '16px' }}
               alignItems={{ base: 'flex-start', sm: 'baseline' }}
               flexDir={{ base: 'column-reverse', sm: 'row' }}
            >
               <Heading
                  fontSize={{ base: '20px', sm: '24px' }}
                  fontWeight="500"
                  color="gray.800"
                  display="flex"
                  margin="0"
               >
                  {_js('Introduction')}
               </Heading>
               <GoToFirstStep margin={0} paddingBottom={{ base: 3, sm: 0 }} />
            </HStack>
            <Box
               dangerouslySetInnerHTML={{ __html: introHtml }}
               ref={introRef}
               sx={{ '& > *::first-of-type': { padding: 0, margin: 0 } }}
            ></Box>
         </Flex>
      </Flex>
   );
}

function GoToFirstStep(props: LinkProps & TextProps) {
   const firstStep = document.querySelectorAll('.step-title')?.[0];

   if (!firstStep) {
      return null;
   }

   const scrollToFirstStep = () => {
      trackAnalyticsCustomEvent({
         eventCategory: 'Guide Products',
         eventAction: 'Guide Products - Go to step 1',
      });
      firstStep?.scrollIntoView({ behavior: 'smooth' });
   };

   return (
      <Text
         as={Link}
         onClick={scrollToFirstStep}
         fontSize="lg"
         textColor="brand.500"
         noOfLines={1}
         {...props}
      >
         {_js('Go to step 1') + ' '}
         <FontAwesomeIcon fontSize="16px" icon={faArrowDown} />
      </Text>
   );
}

function GuideItems({
   hasIntro,
   tools,
   parts,
   kits,
   introRef,
}: GuideProductsData & { hasIntro: boolean; introRef: MutableRefObject<HTMLDivElement> }) {
   const hasProducts = [tools, parts, kits].reduce((sum, arr) => sum + arr.length, 0) > 0;
   if (!hasProducts) {
      return null;
   }

   return (
      <Flex
         flexDir="column"
         paddingY={4}
         paddingRight={{ sm: 0 }}
         paddingLeft={{ sm: hasIntro ? 4 : 0 }}
         flex={{ base: '0 1 50vw', lg: '0 1 448px' }}
      >
         {/* Translators: Heading for a list of parts and tools needed to complete a guide */}
         <Heading
            fontSize={{ base: '20px', sm: '24px' }}
            fontWeight="500"
            color="gray.800"
            marginTop="0px"
            marginBottom={{ base: '12px', sm: '16px' }}
         >
            {_js('What you need')}
         </Heading>
         <GuideProductsSection kits={kits} tools={tools} parts={parts} introRef={introRef} />
      </Flex>
   );
}
